import { Component, Input, forwardRef, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { FillInFieldConfig, FillInField, FillInRow, ParsedTemplate, TemplatePart, ParsedRow, MultiRowFillInValue } from './fill-in-field.interface';

@Component({
  selector: 'app-fill-in-field',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './fill-in-field.component.html',
  styleUrls: ['./fill-in-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FillInFieldComponent),
      multi: true,
    },
  ],
})
export class FillInFieldComponent implements ControlValueAccessor, OnInit, OnChanges {
  @Input() label: string = '';
  @Input() id: string = '';
  @Input() name: string = '';
  @Input() template: string = '';  // 向後相容：單一模板
  @Input() rows: FillInRow[] = []; // 新增：多列模板配置
  @Input() fields: FillInField[] = [];
  @Input() required: boolean = false;
  @Input() preview: boolean = false;

  // 單列模式（向後相容）
  parsedTemplate: ParsedTemplate = { parts: [] };

  // 多列模式
  parsedRows: ParsedRow[] = [];

  // 資料儲存
  fieldValues: { [key: string]: any } = {};
  checkboxStates: { [rowId: string]: boolean } = {};
  value: MultiRowFillInValue | string = '';

  onChange = (value: any) => {};
  onTouched = () => {};

  ngOnInit() {
    this.initializeComponent();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['template'] || changes['rows'] || changes['fields']) {
      this.initializeComponent();
    }
  }

  /**
   * 初始化元件（根據是否有 rows 決定使用多列或單列模式）
   */
  initializeComponent(): void {

    if (this.rows && this.rows.length > 0) {
      // 多列模式
      this.parseMultiRowTemplate();
    } else if (this.template) {
      // 單列模式（向後相容）
      this.parseSingleTemplate();
    } else {
      // 空狀態
      this.parsedTemplate = { parts: [] };
      this.parsedRows = [];
    }

    // 如果已經有值，初始化後更新狀態
    if (this.checkboxStates && Object.keys(this.checkboxStates).length > 0) {
      this.updateParsedRowsState();
    }
  }

  /**
   * 解析單一模板（向後相容）
   */
  parseSingleTemplate(): void {
    if (!this.template) {
      this.parsedTemplate = { parts: [] };
      return;
    }

    const parts: TemplatePart[] = [];
    const regex = /\[([^\]]+)\]/g;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(this.template)) !== null) {
      // 添加前面的文字部分
      if (match.index > lastIndex) {
        const textContent = this.template.substring(lastIndex, match.index);
        if (textContent) {
          parts.push({
            type: 'text',
            content: textContent
          });
        }
      }

      // 添加填空格部分
      const fieldId = match[1];
      const fieldConfig = this.fields.find(f => f.id === fieldId);
      parts.push({
        type: 'field',
        content: match[0],
        fieldId: fieldId,
        fieldConfig: fieldConfig
      });

      lastIndex = regex.lastIndex;
    }

    // 添加最後的文字部分
    if (lastIndex < this.template.length) {
      const textContent = this.template.substring(lastIndex);
      if (textContent) {
        parts.push({
          type: 'text',
          content: textContent
        });
      }
    }

    this.parsedTemplate = { parts };
  }

  /**
   * 解析多列模板
   */
  parseMultiRowTemplate(): void {
    this.parsedRows = this.rows.map(row => {
      // 初始化 checkbox 狀態
      if (this.checkboxStates[row.id] === undefined) {
        this.checkboxStates[row.id] = true; // 預設勾選
      }

      return {
        id: row.id,
        hasCheckbox: row.hasCheckbox,
        checkboxLabel: row.checkboxLabel,
        isChecked: this.checkboxStates[row.id],
        template: this.parseRowTemplate(row.template)
      };
    });
  }

  /**
   * 解析單一列的模板
   */
  parseRowTemplate(template: string): ParsedTemplate {
    if (!template) {
      return { parts: [] };
    }

    const parts: TemplatePart[] = [];
    const regex = /\[([^\]]+)\]/g;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(template)) !== null) {
      // 添加前面的文字部分
      if (match.index > lastIndex) {
        const textContent = template.substring(lastIndex, match.index);
        if (textContent) {
          parts.push({
            type: 'text',
            content: textContent
          });
        }
      }

      // 添加填空格部分
      const fieldId = match[1];
      let fieldConfig = this.fields?.find(f => f.id === fieldId);
      
      // 如果找不到 fieldConfig，創建一個預設配置
      if (!fieldConfig) {
        fieldConfig = {
          id: fieldId,
          type: 'text',
          placeholder: `請輸入${fieldId}`
        };
      }
      
      parts.push({
        type: 'field',
        content: match[0],
        fieldId: fieldId,
        fieldConfig: fieldConfig
      });

      lastIndex = regex.lastIndex;
    }

    // 添加最後的文字部分
    if (lastIndex < template.length) {
      const textContent = template.substring(lastIndex);
      if (textContent) {
        parts.push({
          type: 'text',
          content: textContent
        });
      }
    }

    return { parts };
  }

  /**
   * 處理 Checkbox 狀態變更
   */
  onCheckboxChange(rowId: string, event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    const checked = checkbox?.checked || false;

    this.checkboxStates[rowId] = checked;

    // 更新對應的 parsedRow 狀態
    const parsedRow = this.parsedRows.find(row => row.id === rowId);
    if (parsedRow) {
      parsedRow.isChecked = checked;
    }

    this.updateValue();
    this.onTouched();
  }

  /**
   * 處理填空格值變更
   */
  onFieldValueChange(fieldId: string, value: any): void {
    this.fieldValues[fieldId] = value;
    this.updateValue();
    this.onTouched();
  }

  /**
   * 更新完整的值
   */
  updateValue(): void {
    if (this.rows && this.rows.length > 0) {
      // 多列模式：返回結構化資料和完整文字結果
      const result: MultiRowFillInValue = {
        checkboxStates: { ...this.checkboxStates },
        fieldValues: { ...this.fieldValues },
        completeText: this.generateCompleteText(),
        completeHtml: this.generateCompleteHtml()
      };
      this.value = result;
      this.onChange(this.value);
    } else {
      // 單列模式：返回字串（向後相容）
      this.updateSingleValue();
    }
  }

  /**
   * 更新單列模式的值（向後相容）
   */
  updateSingleValue(): void {
    if (!this.parsedTemplate.parts.length) {
      this.value = '';
      this.onChange(this.value);
      return;
    }

    let result = '';
    for (const part of this.parsedTemplate.parts) {
      if (part.type === 'text') {
        result += part.content;
      } else if (part.type === 'field' && part.fieldId) {
        const fieldValue = this.fieldValues[part.fieldId];
        result += fieldValue || '';
      }
    }

    this.value = result;
    this.onChange(this.value);
  }

  /**
   * 檢查該列的欄位是否應該顯示為填空格
   */
  shouldShowFieldInput(rowId: string): boolean {
    const parsedRow = this.parsedRows.find(row => row.id === rowId);
    return !parsedRow?.hasCheckbox || parsedRow?.isChecked;
  }

  /**
   * 獲取填空格的 CSS 類別
   */
  getFieldClass(fieldConfig?: FillInField): string {
    if (!fieldConfig) return 'fill-in-field';

    const widthClass = fieldConfig.width || 'md';
    return `fill-in-field ${widthClass}`;
  }

  /**
   * 獲取填空格的輸入類型
   */
  getInputType(fieldConfig?: FillInField): string {
    if (!fieldConfig) return 'text';
    return fieldConfig.type;
  }

  /**
   * 獲取填空格的佔位符
   */
  getPlaceholder(fieldConfig?: FillInField): string {
    return fieldConfig?.placeholder || '';
  }

  /**
   * 是否為多列模式
   */
  isMultiRowMode(): boolean {
    return this.rows && this.rows.length > 0;
  }

  /**
   * 是否有模板配置
   */
  hasTemplate(): boolean {
    return !!(this.template && this.template.trim() !== '');
  }

  /**
   * 是否有已完成的內容
   */
  hasCompletedContent(): boolean {
    if (typeof this.value === 'object' && this.value !== null) {
      const multiRowValue = this.value as any;
      return !!(multiRowValue.completeText || multiRowValue.completeHtml);
    }
    return false;
  }

  /**
   * 獲取已完成的 HTML 內容
   */
  getCompletedHtml(): string | null {
    if (typeof this.value === 'object' && this.value !== null) {
      const multiRowValue = this.value as any;
      return multiRowValue.completeHtml || null;
    }
    return null;
  }

  /**
   * 獲取已完成的純文字內容
   */
  getCompletedText(): string | null {
    if (typeof this.value === 'object' && this.value !== null) {
      const multiRowValue = this.value as any;
      return multiRowValue.completeText || null;
    }
    return null;
  }

  /**
   * 獲取摘要文字
   */
  getSummaryText(): string | null {
    if (typeof this.value === 'object' && this.value !== null) {
      const multiRowValue = this.value as any; // 使用 any 來允許存取可能存在的 summary 屬性
      return multiRowValue.summary || null;
    }
    return null;
  }

  /**
   * TrackBy 函數用於 ngFor 優化
   */
  trackByRowId(index: number, row: ParsedRow): string {
    return row.id;
  }

  // ControlValueAccessor 實作
  writeValue(value: any): void {

    if (typeof value === 'object' && value !== null) {
      // 檢查是否有 checkboxStates 和 fieldValues 在頂層
      if ('checkboxStates' in value && 'fieldValues' in value) {
        const multiRowValue = value as MultiRowFillInValue;
        this.checkboxStates = { ...multiRowValue.checkboxStates };
        this.fieldValues = { ...multiRowValue.fieldValues };
        this.value = multiRowValue;

        // 更新 parsedRows 的狀態（如果已經初始化的話）
        this.updateParsedRowsState();
        
        // 如果 parsedRows 還沒初始化，但我們有 rows 配置，重新初始化
        if ((!this.parsedRows || this.parsedRows.length === 0) && this.rows && this.rows.length > 0) {
          this.initializeComponent();
        }
      } 
      // 檢查是否在 originalValue 中
      else if (value.originalValue && typeof value.originalValue === 'object') {
        if ('checkboxStates' in value.originalValue && 'fieldValues' in value.originalValue) {
          this.checkboxStates = { ...value.originalValue.checkboxStates };
          this.fieldValues = { ...value.originalValue.fieldValues };
          this.value = value.originalValue;

          // 更新 parsedRows 的狀態（如果已經初始化的話）
          this.updateParsedRowsState();
          
          // 如果 parsedRows 還沒初始化，但我們有 rows 配置，重新初始化
          if ((!this.parsedRows || this.parsedRows.length === 0) && this.rows && this.rows.length > 0) {
            this.initializeComponent();
          }
        } else {
          this.value = value;
        }
      }
      // 其他物件格式
      else {
        this.value = value;
      }
    } else {
      // 單列模式或舊資料格式
      this.value = value || '';
      this.parseValueToFields(this.value as string);
    }
  }

  /**
   * 更新 parsedRows 的狀態
   */
  private updateParsedRowsState(): void {

    if (this.parsedRows && this.parsedRows.length > 0) {
      this.parsedRows.forEach(row => {
        if (this.checkboxStates[row.id] !== undefined) {
          row.isChecked = this.checkboxStates[row.id];
        }
      });
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    // 可在此處添加禁用邏輯
  }

  /**
   * 解析值到各個填空格（用於從現有值中恢復填空格內容）
   */
  private parseValueToFields(value: string): void {
    if (!value || !this.template) return;

    // 這裡可以實作從完整字串中解析出各個填空格的值
    // 目前先簡單處理，實際使用時可能需要更複雜的邏輯
    this.fieldValues = {};
  }

  /**
   * 產生完整的純文字結果
   */
  private generateCompleteText(): string {
    const results: string[] = [];

    for (const parsedRow of this.parsedRows) {
      // 檢查是否應該包含這一行
      if (parsedRow.hasCheckbox && !parsedRow.isChecked) {
        continue; // 跳過未勾選的行
      }

      // 組合這一行的文字
      let rowText = '';
      for (const part of parsedRow.template.parts) {
        if (part.type === 'text') {
          rowText += part.content;
        } else if (part.type === 'field' && part.fieldId) {
          const fieldValue = this.fieldValues[part.fieldId];
          rowText += fieldValue || '';
        }
      }

      if (rowText.trim()) {
        results.push(rowText.trim());
      }
    }

    // 將多個勾選項目組成連續的文字
    return this.joinTextResults(results);
  }

  /**
   * 智慧地連接多個文字結果
   */
  private joinTextResults(results: string[]): string {
    if (results.length === 0) {
      return '';
    }

    if (results.length === 1) {
      return results[0];
    }

    // 對於多個結果，使用智慧連接
    const joinedResults: string[] = [];

    for (let i = 0; i < results.length; i++) {
      let text = results[i];
      
      // 清理文字：移除開頭和結尾的標點符號（如果需要重新連接）
      if (i > 0) {
        // 如果前一句已經有結尾標點，這句可以移除開頭的多餘空格
        text = text.replace(/^[，、；。！？\s]+/, '');
      }

      // 如果不是最後一句，且沒有結尾標點，加上適當的連接符
      if (i < results.length - 1) {
        // 檢查是否已經有結尾標點
        if (!/[，、；。！？]$/.test(text)) {
          // 根據內容判斷使用什麼連接符
          if (text.includes('；') || text.length > 20) {
            text += '；'; // 長句或已包含分號時用分號
          } else {
            text += '，'; // 短句用逗號
          }
        }
      }

      joinedResults.push(text);
    }

    return joinedResults.join('');
  }

  /**
   * 產生完整的 HTML 結果（保留格式）
   */
  private generateCompleteHtml(): string {
    const results: string[] = [];

    for (const parsedRow of this.parsedRows) {
      // 檢查是否應該包含這一行
      if (parsedRow.hasCheckbox && !parsedRow.isChecked) {
        continue; // 跳過未勾選的行
      }

      // 組合這一行的 HTML
      let rowHtml = '';
      for (const part of parsedRow.template.parts) {
        if (part.type === 'text') {
          // 對文字進行 HTML 轉義
          const escapedText = part.content
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#x27;');
          rowHtml += escapedText;
        } else if (part.type === 'field' && part.fieldId) {
          const fieldValue = this.fieldValues[part.fieldId];
          if (fieldValue) {
            // 將填空值用 span 標記包裹，方便樣式化
            const escapedValue = String(fieldValue)
              .replace(/&/g, '&amp;')
              .replace(/</g, '&lt;')
              .replace(/>/g, '&gt;')
              .replace(/"/g, '&quot;')
              .replace(/'/g, '&#x27;');
            rowHtml += `<span class="fill-in-value">${escapedValue}</span>`;
          }
        }
      }

      if (rowHtml.trim()) {
        results.push(`<div class="fill-in-row">${rowHtml}</div>`);
      }
    }

    return results.join('');
  }
}
